/**
 * The studio's navigation definition — ONE list, consumed by the sidebar and by the route guard.
 *
 * WHY THIS FILE EXISTS. Capability gating used to hide sidebar links and nothing more: `routes/config.ts`
 * carries no capability field and `ProtectedRoute` checks authentication only, so every gated module was
 * reachable by typing its URL. Fixing that needs a path → capability mapping, and the only place that
 * mapping existed was three inline arrays inside `StudioLayout`.
 *
 * Duplicating it into a route guard would have created exactly the drift the brief warns about: add a
 * module to one list, forget the other, and you get a link with no guard or a guard with no link.
 * So the arrays moved here and both consumers read them. There is no second list to fall out of sync.
 *
 * THE ALIASES ARE THE SUBTLE PART. A module's routes are not all under its `href`. `/reports` also owns
 * `/runs` and `/agent-runs/*`; `/org360` also owns `/explorer`; `/studio/git` also owns `/git` and
 * `/version-control`. That knowledge already existed — in `isNavActive`, for highlighting the active
 * link — and a guard built on `href` alone would have left `/runs` completely unguarded while `/reports`
 * was locked. So `ownedPrefixes` is declared once and serves both jobs: which link looks active, and
 * which paths a capability protects.
 */

/** Extra paths a nav item owns beyond its own `href`. Keyed by `href`. */
const OWNED_PREFIXES: Record<string, readonly string[]> = {
  '/migrations': ['/skills'],
  '/reports': ['/runs', '/agent-runs'],
  '/salesforce-releases': ['/release-scanner'],
  '/org360': ['/explorer'],
  '/studio/git': ['/git', '/version-control'],
};

export interface StudioNavDefinition {
  label: string;
  href: string;
  /**
   * The capability required to see the link AND to enter any path this item owns.
   *
   * Absent means ungated — Home, Connection Manager, Deployment and Migrations carry no
   * `moduleKey` today, so they stay reachable by everyone. That is existing product behaviour and this
   * change does not tighten it; doing so silently would lock people out of screens they use.
   */
  moduleKey?: string;
}

/** Icons live with the component that renders them; this file is the routing/authorization contract. */
export const WORKSPACE_NAV: readonly StudioNavDefinition[] = [
  { label: 'Home', href: '/app/dashboard' },
  { label: 'Metadata Explorer', href: '/metadata', moduleKey: 'module:metadata-explorer' },
  { label: 'Documentation', href: '/docs', moduleKey: 'module:documentation' },
  { label: 'Text to SOQL', href: '/text-to-soql', moduleKey: 'module:text-to-soql' },
  { label: 'Security Scanner', href: '/security', moduleKey: 'module:security-scanner' },
  { label: 'Connection Manager', href: '/org/connection' },
];

export const AGENT_NAV: readonly StudioNavDefinition[] = [
  { label: 'Agents', href: '/agents', moduleKey: 'module:agents' },
  { label: 'Weave', href: '/weave', moduleKey: 'module:weave' },
  { label: 'Weave 2', href: '/weave2', moduleKey: 'module:weave' },
  { label: 'Migrations', href: '/migrations' },
  { label: 'Scheduling', href: '/scheduling', moduleKey: 'module:scheduling' },
];

export const DELIVERY_NAV: readonly StudioNavDefinition[] = [
  { label: 'Reports', href: '/reports', moduleKey: 'module:reports' },
  { label: 'MCP', href: '/tools', moduleKey: 'module:mcp' },
  { label: 'Git & pipelines', href: '/studio/git', moduleKey: 'module:git-pipelines' },
  { label: 'Org 360', href: '/org360', moduleKey: 'module:org-360' },
  { label: 'Salesforce releases', href: '/salesforce-releases', moduleKey: 'module:salesforce-releases' },
  { label: 'Deployment', href: '/deployment' },
  { label: 'Testing', href: '/testing', moduleKey: 'module:testing' },
  { label: 'Salesforce Debugger', href: '/debugging', moduleKey: 'module:salesforce-debugger' },
];

/**
 * The migrations screens.
 *
 * Declared separately because `MIGRATIONS_NAV_ITEMS` is exported for a surface that does not render in
 * the sidebar today — but the guard must still know these paths belong to `module:migrations`, or they
 * would be reachable by URL with no capability at all.
 */
export const MIGRATIONS_MODULE_KEY = 'module:migrations';

export const MIGRATIONS_NAV: readonly StudioNavDefinition[] = [
  { label: 'Migration Engine', href: '/migration-engine', moduleKey: MIGRATIONS_MODULE_KEY },
  { label: 'Portfolio Pass', href: '/migration-portfolio', moduleKey: MIGRATIONS_MODULE_KEY },
];

/** Every nav definition the app knows about, gated or not. */
export const ALL_NAV: readonly StudioNavDefinition[] = [
  ...WORKSPACE_NAV,
  ...AGENT_NAV,
  ...DELIVERY_NAV,
  ...MIGRATIONS_NAV,
];

/** Every path prefix one nav item owns, longest first so the most specific wins. */
export function ownedPrefixes(item: StudioNavDefinition): readonly string[] {
  return [item.href, ...(OWNED_PREFIXES[item.href] ?? [])];
}

/**
 * The item that owns `pathname` most SPECIFICALLY, or null.
 *
 * WHY THIS EXISTS. `ownsPath` answers per item, and two items can both own one path: `/weave` owns
 * `/weave/v1` by the segment-aware prefix rule, so the moment `/weave/v1` became its own nav row both
 * links read as active at once. Highlighting needs one winner, and the winner is the longest matched
 * prefix -- the same "most specific wins" rule `capabilityForPath` applies to authorization.
 *
 * Deliberately NOT shared with `capabilityForPath`: that function arbitrates on `item.href` and is a
 * security boundary, so it is left exactly as it is. This one is for chrome.
 */
export function mostSpecificOwner<T extends StudioNavDefinition>(
  items: readonly T[],
  pathname: string,
): T | null {
  let best: T | null = null;
  let bestLength = -1;

  for (const item of items) {
    for (const prefix of ownedPrefixes(item)) {
      if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) continue;
      if (prefix.length > bestLength) {
        best = item;
        bestLength = prefix.length;
      }
    }
  }

  return best;
}

/**
 * Whether `pathname` falls inside a nav item's territory.
 *
 * Prefix matching is segment-aware: `/agent` must not match `/agents`, and `/orgs` must not match
 * `/org`. A bare `startsWith` gets that wrong in both directions — it would gate an unrelated route
 * and, worse, could leave a real one ungated because a shorter item claimed it first.
 */
export function ownsPath(item: StudioNavDefinition, pathname: string): boolean {
  return ownedPrefixes(item).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
